import { RichTextNode, RichTextNodeKind } from '@griply/types';

import { getUrlScheme } from '../sanitize-external-url';
import { decodeHtmlEntities } from './html-entities';
import { plainTextToHtml } from './plain-text-to-html';

// The `$` alternative is what makes an unterminated comment swallow the rest rather than fall to text.
const tagRegex =
  /<!--[\s\S]*?(?:-->|$)|<(\/)?([a-z][\w:-]*)((?:\s[^<>]*)?)\/?>/gi;
const hrefRegex = /\bhref=["']([^"']*)["']/i;

// Wider than sanitizeExternalUrl's: a description may legitimately link to an address or number.
const ALLOWED_HREF_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

// Provider descriptions are attacker-controlled, so a javascript: href must never reach the DOM.
const sanitizeHref = (href: string): string => {
  const scheme = getUrlScheme(href);

  if (!scheme) {
    return '';
  }

  return ALLOWED_HREF_PROTOCOLS.includes(scheme) ? href : '';
};

// Only a recognisable tag counts as markup, so a description reading "5 < 10" is still text.
const htmlTagRegex = /<\/?[a-z][\w-]*(?:\s[^<>]*)?\/?>/i;

export const containsHtmlMarkup = (value: string): boolean =>
  htmlTagRegex.test(value);

// Anything outside this list is unwrapped, not dropped: a <span> keeps its text, minus the node.
const containerTags = [
  'p',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'a',
  'b',
  'strong',
  'i',
  'em',
  'u',
];

// Raw-text elements: their content is stylesheet or script source, never something to read.
const nonContentTags = ['script', 'style', 'title'];

type OpenNode = { tag: string; href: string; children: RichTextNode[] };

const toNode = ({ tag, href, children }: OpenNode): RichTextNode => {
  switch (tag) {
    case 'ul':
      return { kind: RichTextNodeKind.List, ordered: false, children };
    case 'ol':
      return { kind: RichTextNodeKind.List, ordered: true, children };
    case 'li':
      return { kind: RichTextNodeKind.ListItem, children };
    case 'a':
      return { kind: RichTextNodeKind.Link, href, children };
    case 'b':
    case 'strong':
      return { kind: RichTextNodeKind.Bold, children };
    case 'i':
    case 'em':
      return { kind: RichTextNodeKind.Italic, children };
    case 'u':
      return { kind: RichTextNodeKind.Underline, children };
    case 'p':
      return { kind: RichTextNodeKind.Paragraph, children };
    default:
      return { kind: RichTextNodeKind.Block, children };
  }
};

export const parseRichText = (value: string): RichTextNode[] => {
  if (!value) {
    return [];
  }

  // In plain text a newline is a line break; read as HTML it collapses away as whitespace.
  const markup = containsHtmlMarkup(value) ? value : plainTextToHtml(value);

  const root: RichTextNode[] = [];
  const open: OpenNode[] = [];

  const currentChildren = () =>
    open.length ? open[open.length - 1].children : root;

  // An unclosed tag must not swallow the rest of the document, so closing one also closes
  // everything opened inside it.
  const closeThrough = (depth: number) => {
    const count = open.length - depth;
    if (count <= 0) {
      return;
    }
    Array.from({ length: count }).forEach(() => {
      const node = open.pop()!;
      currentChildren().push(toNode(node));
    });
  };

  const pushText = (text: string) => {
    if (!text) {
      return;
    }
    currentChildren().push({
      kind: RichTextNodeKind.Text,
      value: decodeHtmlEntities(text),
    });
  };

  let lastIndex = 0;
  let suppressed: string | null = null;

  [...markup.matchAll(tagRegex)].forEach((match) => {
    const matchIndex = match.index ?? 0;
    if (!suppressed) {
      pushText(markup.substring(lastIndex, matchIndex));
    }
    lastIndex = matchIndex + match[0].length;

    const isClosing = !!match[1];
    // Absent on the comment alternative, which has consumed its own span and models nothing.
    const tag = match[2]?.toLowerCase();

    if (!tag) {
      return;
    }

    if (suppressed) {
      if (isClosing && tag === suppressed) {
        suppressed = null;
      }
      return;
    }

    if (nonContentTags.includes(tag)) {
      if (!isClosing) {
        suppressed = tag;
      }
      return;
    }

    if (tag === 'br') {
      !isClosing && currentChildren().push({ kind: RichTextNodeKind.Break });
      return;
    }

    if (!containerTags.includes(tag)) {
      return;
    }

    if (!isClosing) {
      open.push({
        tag,
        href: sanitizeHref(
          decodeHtmlEntities(match[3]?.match(hrefRegex)?.[1] ?? ''),
        ),
        children: [],
      });
      return;
    }

    const openIndex = open.reduce(
      (found, node, index) => (node.tag === tag ? index : found),
      -1,
    );
    openIndex !== -1 && closeThrough(openIndex);
  });

  pushText(suppressed ? '' : markup.substring(lastIndex));
  closeThrough(0);

  return root;
};
